import React, { useState, useEffect, useRef } from 'react';
import { X, Key, Mail, Save, Check } from 'lucide-react';
import { getEnvVar } from '../../config/env';

const API_BASE_URL = getEnvVar('VITE_FASTAPI_BASE_URL');

const PROVIDER_OPTIONS = [
  { provider_type: 'voice', provider_name: 'elevenlabs', label: 'ElevenLabs (Voice)' },
  { provider_type: 'voice', provider_name: 'retell', label: 'Retell (Voice & Phone)' },
  { provider_type: 'phone', provider_name: 'telnyx', label: 'Telnyx (Phone)' },
  { provider_type: 'llm', provider_name: 'openai', label: 'OpenAI (LLM)' },
  { provider_type: 'llm', provider_name: 'anthropic', label: 'Anthropic (LLM)' },
  { provider_type: 'llm', provider_name: 'google', label: 'Google (LLM)' },
  { provider_type: 'email', provider_name: 'mailgun', label: 'Mailgun (Email Campaigns)' },
  { provider_type: 'email', provider_name: 'resend', label: 'Resend (Email Campaigns)' },
];

interface ApiKeyOption {
  id: string;
  name: string;
  is_global: boolean;
}

interface WorkspaceAssignment {
  provider_type: string;
  provider_name: string;
  api_key_id: string;
}

interface WorkspaceApiKeyModalProps {
  workspaceId: string;
  workspaceName: string;
  isOpen: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

export const WorkspaceApiKeyModal: React.FC<WorkspaceApiKeyModalProps> = ({
  workspaceId,
  workspaceName,
  isOpen,
  onClose,
  onSaved,
}) => {
  const [keysByProvider, setKeysByProvider] = useState<Record<string, ApiKeyOption[]>>({});
  const [assignments, setAssignments] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Track the server-side state so we can diff on save
  const savedAssignments = useRef<Record<string, string>>({});

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('admin_auth_token')}`,
    'Content-Type': 'application/json',
  });

  const getKey = (pt: string, pn: string) => `${pt}:${pn}`;

  useEffect(() => {
    if (isOpen && workspaceId) {
      loadAll();
    }
  }, [isOpen, workspaceId]);

  const loadAll = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      const [assignRes, keysRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/v1/admin/workspaces/${workspaceId}/provider-keys`, { headers: getAuthHeaders() }),
        fetch(`${API_BASE_URL}/api/v1/admin/api-keys`, { headers: getAuthHeaders() }),
      ]);
      if (!assignRes.ok) throw new Error('Failed to load workspace keys');
      if (!keysRes.ok) throw new Error('Failed to load API keys');
      const assignData = await assignRes.json();
      const keysData = await keysRes.json();
      const allKeys: Array<{ provider_type: string; provider_name: string; id: string; name: string; is_global: boolean }> = keysData.data || [];
      const assignList: WorkspaceAssignment[] = assignData.data || [];

      const byProvider: Record<string, ApiKeyOption[]> = {};
      PROVIDER_OPTIONS.forEach((p) => {
        const key = getKey(p.provider_type, p.provider_name);
        byProvider[key] = allKeys
          .filter((k: { provider_type: string; provider_name: string }) => k.provider_type === p.provider_type && k.provider_name === p.provider_name)
          .map((k: { id: string; name: string; is_global: boolean }) => ({ id: k.id, name: k.name, is_global: k.is_global }));
      });

      const assignMap: Record<string, string> = {};
      assignList.forEach((a: WorkspaceAssignment) => {
        assignMap[getKey(a.provider_type, a.provider_name)] = a.api_key_id;
      });

      setKeysByProvider(byProvider);
      setAssignments(assignMap);
      savedAssignments.current = { ...assignMap };
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  const getSelectedKeyId = (pt: string, pn: string) => assignments[getKey(pt, pn)];

  const getGlobalKeyId = (pt: string, pn: string) => {
    const k = keysByProvider[getKey(pt, pn)] || [];
    return k.find((x) => x.is_global)?.id;
  };

  const handleLocalSelect = (provider: typeof PROVIDER_OPTIONS[0], apiKeyId: string) => {
    const key = getKey(provider.provider_type, provider.provider_name);
    setAssignments((s) => ({ ...s, [key]: apiKeyId }));
    setSuccess(null);
  };

  const hasUnsavedChanges = () => {
    return PROVIDER_OPTIONS.some((p) => {
      const key = getKey(p.provider_type, p.provider_name);
      const options = keysByProvider[key] || [];
      if (options.length === 0) return false;
      return (assignments[key] || '') !== (savedAssignments.current[key] || '');
    });
  };

  const handleSaveAll = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    const errors: string[] = [];
    let savedCount = 0;

    for (const provider of PROVIDER_OPTIONS) {
      const key = getKey(provider.provider_type, provider.provider_name);
      const options = keysByProvider[key] || [];
      if (options.length === 0) continue;

      const currentVal = assignments[key] || '';
      const savedVal = savedAssignments.current[key] || '';
      if (currentVal === savedVal) continue;

      const globalId = getGlobalKeyId(provider.provider_type, provider.provider_name);

      try {
        if (currentVal === globalId || !currentVal) {
          const res = await fetch(
            `${API_BASE_URL}/api/v1/admin/workspaces/${workspaceId}/provider-keys/${provider.provider_type}/${provider.provider_name}`,
            { method: 'DELETE', headers: getAuthHeaders() }
          );
          if (!res.ok) throw new Error(`Failed to reset ${provider.label}`);
        } else {
          const res = await fetch(`${API_BASE_URL}/api/v1/admin/workspaces/${workspaceId}/provider-keys`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify({
              provider_type: provider.provider_type,
              provider_name: provider.provider_name,
              api_key_id: currentVal,
            }),
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data.detail || data.error || `Failed to save ${provider.label}`);
        }
        savedAssignments.current[key] = currentVal;
        savedCount++;
      } catch (err) {
        errors.push(err instanceof Error ? err.message : `Failed to save ${provider.label}`);
      }
    }

    setSaving(false);

    if (errors.length > 0) {
      setError(errors.join('. '));
    }
    if (savedCount > 0) {
      setSuccess(`${savedCount} provider key${savedCount > 1 ? 's' : ''} saved successfully.`);
      onSaved?.();
    } else if (errors.length === 0) {
      setSuccess('No changes to save.');
    }
  };

  if (!isOpen) return null;

  const unsaved = hasUnsavedChanges();

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={onClose} />
        <div className="relative bg-white rounded-lg shadow-xl max-w-xl w-full max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 pb-4 border-b border-gray-200">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Workspace API Keys</h3>
              <p className="text-sm text-gray-500 mt-1">{workspaceName}</p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto p-6">
            <p className="text-sm text-gray-600 mb-4">
              Select which API key to use for each provider. Only keys from the API Keys Master are shown. Default is the global key.
            </p>

            {error && <div className="mb-4 p-3 rounded-md bg-red-50 text-red-700 text-sm">{error}</div>}
            {success && <div className="mb-4 p-3 rounded-md bg-green-50 text-green-700 text-sm flex items-center gap-2"><Check className="h-4 w-4" />{success}</div>}

            {loading ? (
              <div className="py-8 text-center text-gray-500">Loading...</div>
            ) : (
              <div className="space-y-6">
                {(['voice', 'phone', 'llm', 'email'] as const).map((groupType) => {
                  const groupProviders = PROVIDER_OPTIONS.filter((p) => p.provider_type === groupType);
                  if (groupProviders.length === 0) return null;
                  const groupLabels: Record<string, string> = {
                    voice: 'Voice',
                    phone: 'Phone / SMS',
                    llm: 'LLM / AI',
                    email: 'Email Campaigns',
                  };
                  return (
                    <div key={groupType}>
                      <div className="flex items-center gap-2 mb-3">
                        {groupType === 'email' ? (
                          <Mail className="h-4 w-4 text-blue-500" />
                        ) : (
                          <Key className="h-4 w-4 text-gray-400" />
                        )}
                        <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                          {groupLabels[groupType] || groupType}
                        </h4>
                      </div>
                      <div className="space-y-3">
                        {groupProviders.map((provider) => {
                          const key = getKey(provider.provider_type, provider.provider_name);
                          const options = keysByProvider[key] || [];
                          const selectedId = getSelectedKeyId(provider.provider_type, provider.provider_name);
                          const globalId = getGlobalKeyId(provider.provider_type, provider.provider_name);
                          const isChanged = (assignments[key] || '') !== (savedAssignments.current[key] || '');

                          return (
                            <div
                              key={key}
                              className={`p-4 rounded-lg border space-y-3 ${isChanged ? 'border-blue-400 bg-blue-50/30' : 'border-gray-200'}`}
                            >
                              <div className="flex items-center gap-2">
                                <Key className="h-4 w-4 text-gray-400" />
                                <span className="text-sm font-medium text-gray-900">{provider.label}</span>
                                {isChanged && (
                                  <span className="ml-auto text-xs text-blue-600 font-medium">unsaved</span>
                                )}
                              </div>

                              {options.length === 0 ? (
                                <p className="text-sm text-gray-500">No keys in API Keys Master. Add keys there first.</p>
                              ) : (
                                <div>
                                  <label className="block text-xs font-medium text-gray-500 mb-1">Select API Key</label>
                                  <select
                                    value={selectedId || ''}
                                    onChange={(e) => handleLocalSelect(provider, e.target.value)}
                                    disabled={saving}
                                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm py-2"
                                  >
                                    <option value="">Use global (default)</option>
                                    {options.map((opt) => (
                                      <option key={opt.id} value={opt.id}>
                                        {opt.name} {opt.is_global ? '(Global)' : ''}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer with Save / Cancel */}
          <div className="flex items-center justify-end gap-3 p-6 pt-4 border-t border-gray-200">
            <button
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveAll}
              disabled={saving || !unsaved}
              className={`inline-flex items-center gap-2 px-5 py-2 text-sm font-medium text-white rounded-md disabled:opacity-50 ${
                unsaved
                  ? 'bg-blue-600 hover:bg-blue-700'
                  : 'bg-gray-400 cursor-not-allowed'
              }`}
            >
              {saving ? (
                <>
                  <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
